/**
 * Which shop the dashboard is acting on, shown persistently in the header — never behind a menu
 * with nothing on screen to say which one is current. A user must never be unsure which shop
 * they're about to write stock into.
 *
 * Only a click for someone holding `shop.act_any` (the Owner, by default — see
 * `PermissionSyncService`, which re-syncs the Owner role against the full permission catalogue
 * on every boot). Everyone else gets a plain label: their own home shop, fixed, no control to
 * mis-tap. The backend enforces this the same way — `InventoryEndpoints.ForbidCrossShop` refuses
 * any shop-scoped request that names a shop other than the caller's own without this same
 * permission — so this menu is a convenience, never the only thing standing between a user and
 * another shop's stock.
 */

import { useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';

import {
  Permission,
  activeShopChanged,
  can,
  selectActiveShopId,
  selectUser,
  useListShopsQuery,
} from '@pos/shared';

import { useAppDispatch, useAppSelector } from '@/app/hooks';

export function ShopSwitcher() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const activeShopId = useAppSelector(selectActiveShopId);
  const mayActAny = can(user, Permission.ShopActAny);

  const { data: shops } = useListShopsQuery(undefined, { skip: !mayActAny });
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const activeName =
    (mayActAny ? shops : undefined)?.find((shop) => shop.id === activeShopId)?.name ??
    user?.homeShopName ??
    '—';

  if (!mayActAny) {
    return (
      <Box data-tour="shop" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
        <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {activeName}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ButtonBase
        data-tour="shop"
        onClick={(event) => {
          setAnchor(event.currentTarget);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 2,
          '&:hover': { bgcolor: 'var(--pos-surface-sunken)' },
        }}
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {activeName}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {(shops ?? []).map((shop) => (
          <MenuItem
            key={shop.id}
            selected={shop.id === activeShopId}
            onClick={() => {
              dispatch(activeShopChanged(shop.id));
              setAnchor(null);
            }}
          >
            {shop.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
